import type { Atom } from 'jotai'
import type { Ability } from '@/features/spirit/requests'
import type { AbilitySummary, Spirit } from '@phalaworld/types/phalaworld'

import { atom } from 'jotai'
import { atomWithQuery, queryClientAtom } from 'jotai/query'
import { propOr, pathOr } from 'ramda'

import { apiPromiseAtom } from '@/features/parachain/atoms'
import { queryConstants } from '@/features/parachain/queries'
import { querySpiritAttributes, querySpiritCollectionId, queryOwnedCollectionAssets } from '@/features/spirit/queries'
import { currentAccountAtom } from '@/features/identity/atoms'

import { pwapiEndpointPrefixAtom } from './hosts'
import { SpiritMetadata } from '../constants'


export const profileAdddressAtom = atom('')

const calcAbilitySummary = (ability: Ability) => {
  const current_level_exp_begin: number = propOr(0, 'current_level_exp_begin', ability)
  const current_level_exp_end: number = propOr(0, 'current_level_exp_end', ability)
  const level: number = propOr(0, 'level', ability)
  const exp: number = propOr(0, 'exp', ability)
  const total = current_level_exp_end - current_level_exp_begin
  const current = exp - current_level_exp_begin
  const progress = current === 0 ? 0 : Math.round(current / total * 100)
  return {
    level,
    exp,
    target: current_level_exp_end,
    current,
    total,
    progress,
  }
}

export function spiritAtomWithAddress(addressAtom: Atom<string | undefined>) {
  return atom(async (get) => {
    const address = get(addressAtom)

    const api = get(apiPromiseAtom)
    const queryClient = get(queryClientAtom)
    const prefix = get(pwapiEndpointPrefixAtom)

    const [attrs, collectionId, constants] = await Promise.all([
      queryClient.fetchQuery(querySpiritAttributes(prefix, address)),
      queryClient.fetchQuery(querySpiritCollectionId(api)),
      queryClient.fetchQuery(queryConstants(prefix)),
    ])

    let nftIds: Pairs<number>[] = []
    if (address) {
      nftIds = await queryClient.fetchQuery(queryOwnedCollectionAssets(api, collectionId, address))
    }

    const [nftId, formattedNftId] = (nftIds.length > 0) ? [nftIds[0][0], nftIds[0][0]] : [null, null]
    return {
      nftId,
      formattedNftId,
      metadata: SpiritMetadata,
      code: attrs.code,
      strength: calcAbilitySummary(pathOr({}, ['stats', 'strength'], attrs) as Ability),
      dexterity: calcAbilitySummary(pathOr({}, ['stats', 'dexterity'], attrs) as Ability),
      intelligent: calcAbilitySummary(pathOr({}, ['stats', 'intellect'], attrs) as Ability),
      willpower: calcAbilitySummary(pathOr({}, ['stats', 'willpower'], attrs) as Ability),
      reputation: calcAbilitySummary(pathOr({}, ['stats', 'reputation'], attrs) as Ability),
      level: pathOr(0, ['stats', 'reputation', 'level'], attrs),
      rank: pathOr(0, ['stats', 'reputation', 'rank'], attrs),
      totalInRank: propOr(0, 'total_spirits_count', constants) as number,
      isWhitelisted: pathOr(false, ['extra', 'is_whitelisted'], attrs),
      power: (attrs.award_points || 0) - (attrs.consume_points || 0),
      dressupNftId: attrs.dressup_nft_id,
    } as Spirit
  })
}

export function transformSpirit(nftId: number | null, spiritRaw: any, totalSpiritCounts: number) {
  return {
    nftId,
    formattedNftId: nftId,
    metadata: SpiritMetadata,
    code: spiritRaw.code,
    strength: calcAbilitySummary(pathOr({}, ['stats', 'strength'], spiritRaw) as Ability),
    dexterity: calcAbilitySummary(pathOr({}, ['stats', 'dexterity'], spiritRaw) as Ability),
    intelligent: calcAbilitySummary(pathOr({}, ['stats', 'intellect'], spiritRaw) as Ability),
    willpower: calcAbilitySummary(pathOr({}, ['stats', 'willpower'], spiritRaw) as Ability),
    reputation: calcAbilitySummary(pathOr({}, ['stats', 'reputation'], spiritRaw) as Ability),
    level: pathOr(0, ['stats', 'reputation', 'level'], spiritRaw),
    rank: pathOr(0, ['stats', 'reputation', 'rank'], spiritRaw),
    // totalInRank: propOr(0, 'total_spirits_count', constants) as number,
    totalInRank: totalSpiritCounts,
    isWhitelisted: pathOr(false, ['extra', 'is_whitelisted'], spiritRaw),
    power: (spiritRaw.award_points || 0) - (spiritRaw.consume_points || 0),
    dressupNftId: spiritRaw.dressup_nft_id,
  } as Spirit
}

const currentAccountAddressAtom = atom(get => get(currentAccountAtom)?.address)

export const spiritAtom = spiritAtomWithAddress(currentAccountAddressAtom)
